/* =========================================================================
   Per-answer submission → POST /api/game/answer (AUTHED ONLY).

   This is the call that finally populates app.UserQuestionHistory, which is what
   makes usp_Question_ServeBatch's "TimesSeen ASC, LastSeenUtc ASC" ordering do
   its job — i.e. it is the thing that ends the silent repeats (PLAN-progression
   §3). It is pure telemetry-grade plumbing: fire-and-forget, never gameplay
   blocking, queued and drained one at a time in answer order (same shape as
   servedLog.ts). Guests never enqueue — their loop stays fully offline.
   ========================================================================= */
import { postGameAnswer } from './api';

export interface AnswerRecord {
  sessionId: string;
  stableCode: string;
  /** -1 when the question timed out (no option chosen). */
  selectedIndex: number;
  correct: boolean;
  elapsedMs: number;
  /** The cents this answer moved the bank by (see api.postGameAnswer). */
  valueCents: number;
}

/** A queued answer poster. Returns false to requeue (retried on the next flush). */
export type AnswerPoster = (a: AnswerRecord) => Promise<boolean>;

const MAX_QUEUE = 80; // a 46-question game plus slack; oldest are dropped past this

let pending: AnswerRecord[] = [];
let inFlight: Promise<void> | null = null;

async function defaultPoster(a: AnswerRecord): Promise<boolean> {
  const r = await postGameAnswer(a);
  // A 4xx means the server rejected this row for good — dropping it is correct
  // (requeueing forever would wedge the queue). Offline/5xx are retried.
  if (r.ok) return true;
  if (!r.offline && typeof r.status === 'number' && r.status >= 400 && r.status < 500) return true;
  return false;
}

let poster: AnswerPoster = defaultPoster;

/** Test seam — swap the transport. Pass nothing to restore the real one. */
export function setAnswerPoster(p?: AnswerPoster): void {
  poster = p ?? defaultPoster;
}

/** Drop everything queued (new game / sign-out). */
export function resetAnswerQueue(): void {
  pending = [];
}

export function pendingAnswerCount(): number {
  return pending.length;
}

/** Queue one answered question and kick the drain. Fire-and-forget by design. */
export function recordAnswer(a: AnswerRecord): void {
  armTeardownFlush();
  pending.push(a);
  if (pending.length > MAX_QUEUE) pending = pending.slice(pending.length - MAX_QUEUE);
  void kick();
}

/** Post everything queued, in order. Failures stay queued for the next flush.
    Awaits any drain already in flight, then makes a fresh pass — so a caller
    that flushes at a round boundary really does see the queue emptied. */
export async function flushAnswers(): Promise<void> {
  if (inFlight) await inFlight;
  await kick();
}

/* A player who quits mid-round would otherwise lose the answers queued since the
   last round boundary — and with them the history rows that stop repeats. Wired
   lazily on the first enqueue (no boot cost, nothing for main.ts to call) on BOTH
   pagehide and visibilitychange→hidden: an iOS/PWA kill often delivers only one. */
let teardownArmed = false;
function armTeardownFlush(): void {
  if (teardownArmed) return;
  teardownArmed = true;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const flush = (): void => void flushAnswers();
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}

/** Start a drain if none is running; otherwise return the running one. */
function kick(): Promise<void> {
  if (!inFlight) {
    inFlight = drain().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function drain(): Promise<void> {
  while (pending.length > 0) {
    const next = pending[0];
    let ok = false;
    try {
      ok = await poster(next);
    } catch {
      ok = false;
    }
    if (!ok) return; // keep it (and everything after it) for the next flush
    // The head may have been reset mid-flight (new game) — only shift our own row.
    if (pending[0] === next) pending.shift();
  }
}
